import type { FC, ReactNode } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { useSelector } from "react-redux";
import type { RootState } from "@store/store";
import { BROWSER_ROUTES } from "@constants/browser.routes";
import { consumePostLogoutRedirect, shouldPreserveAuthRedirect } from "@utils/authRedirect";

interface AuthGuardProps {
	children: ReactNode;
}

const AuthGuard: FC<AuthGuardProps> = ({ children }) => {
	const { isAuthenticated } = useSelector((state: RootState) => state.user);
	const location = useLocation();

	if (!isAuthenticated) {
		if (consumePostLogoutRedirect()) {
			return <Navigate to={BROWSER_ROUTES.LOGIN} state={{ from: location }} replace />;
		}

		const fullPath = `${location.pathname}${location.search}${location.hash}`;
		const params = new URLSearchParams();
		if (shouldPreserveAuthRedirect(fullPath)) {
			params.set("returnTo", fullPath);
		}
		const search = params.toString();
		const loginPath = search ? `${BROWSER_ROUTES.LOGIN}?${search}` : BROWSER_ROUTES.LOGIN;
		return <Navigate to={loginPath} state={{ from: location }} replace />;
	}

	return <>{children}</>;
};

export default AuthGuard;
